import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import confetti from "canvas-confetti";
import { api, API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { safeInvalidateSize, safeFitBounds } from "@/lib/leafletSafe";
import { MapPin, Trophy, Target, Share2, CalendarDays } from "lucide-react";

function rateColor(rate) {
  const h = Math.max(0, Math.min(100, rate)) * 1.4; // 0=red → 140=green
  return `hsl(${h}, 72%, 42%)`;
}

function fireConfetti(intense = false) {
  const opts = { spread: 70, startVelocity: 42, ticks: 200, zIndex: 9999,
    colors: ["#F5B301", "#191A54", "#0E9F6E", "#E4232E", "#ffffff"] };
  confetti({ ...opts, particleCount: intense ? 140 : 70, origin: { x: 0.5, y: 0.35 } });
  if (intense) {
    setTimeout(() => confetti({ ...opts, particleCount: 60, angle: 60, origin: { x: 0, y: 0.6 } }), 150);
    setTimeout(() => confetti({ ...opts, particleCount: 60, angle: 120, origin: { x: 1, y: 0.6 } }), 250);
  }
}

function AnimatedCount({ value, start }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start) return;
    const target = Number(value) || 0;
    if (target <= 0) { setN(0); return; }
    let raf, t0;
    const dur = 1600;
    const tick = (ts) => {
      if (!t0) t0 = ts;
      const p = Math.min(1, (ts - t0) / dur);
      setN(Math.round((1 - Math.pow(1 - p, 4)) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, start]);
  return <b className="text-findo-navy" data-testid="impact-restituted-count">{n}</b>;
}

export const PublicImpactMap = () => {
  const { t } = useLang();
  const TT = (k, d) => (t && t(k) !== k ? t(k) : d);
  const [data, setData] = useState({ cities: [], totals: {} });
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    api("/public/city-impact", { auth: false })
      .then(setData)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!sectionRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold: 0.25 }
    );
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const geo = (data.cities || []).filter((c) => c.lat != null && c.lng != null);

  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    const map = L.map(mapRef.current, { scrollWheelZoom: false, zoomControl: true }).setView([46.6, 2.4], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap", maxZoom: 18,
    }).addTo(map);
    mapObj.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    setTimeout(() => safeInvalidateSize(map), 200);
    return () => { map.remove(); mapObj.current = null; };
  }, []);

  useEffect(() => {
    const map = mapObj.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const maxTotal = Math.max(1, ...geo.map((c) => c.total));
    const bounds = [];
    geo.forEach((c) => {
      const radius = 10 + Math.round((c.total / maxTotal) * 22);
      const color = rateColor(c.rate);
      const reached = c.target != null && (c.progress || 0) >= 100;
      if (reached) {
        L.circleMarker([c.lat, c.lng], {
          radius: radius + 6, color: "#F5B301", weight: 3, opacity: 0.9,
          fillColor: "#F5B301", fillOpacity: 0.12,
        }).addTo(layer);
      }
      const m = L.circleMarker([c.lat, c.lng], {
        radius, color, weight: 2, fillColor: color, fillOpacity: 0.5,
      }).addTo(layer);
      if (reached) m.on("popupopen", () => fireConfetti(false));
      m.bindPopup(
        `<b>${c.city}</b><br/><b style="color:${color}">${c.restituted}</b> ${t("hc.itemsReturned")} · ${c.rate}%` +
        (c.target != null
          ? (reached
              ? `<br/><div style="margin-top:6px;display:inline-block;background:#FEF3C7;color:#92400E;font-weight:700;font-size:12px;padding:3px 10px;border-radius:99px">🎉 ${t("hc.targetReached")} (${c.target}%)</div>`
              : `<br/><div style="margin-top:6px;font-size:12px;color:#475569">${t("hc.target")} ${c.target}% · ` +
                `<b style="color:#191A54">${c.progress || 0}% ${t("hc.reached")}</b></div>` +
                `<div style="margin-top:4px;width:140px;height:6px;border-radius:99px;background:#e2e8f0;overflow:hidden">` +
                `<div style="height:100%;width:${Math.min(100, c.progress || 0)}%;background:#191A54"></div></div>`)
          : "")
      );
      bounds.push([c.lat, c.lng]);
    });
    map.invalidateSize();
    if (bounds.length) safeFitBounds(map, bounds, { padding: [50, 50], maxZoom: 6 });
    setTimeout(() => safeInvalidateSize(map), 150);
  }, [data]); // eslint-disable-line

  async function shareYearRecap() {
    const yr = yearly?.year || new Date().getFullYear();
    const text = t("hc.findoReviewShare").replace("{year}", yr).replace("{n}", yearly?.restituted || 0).replace("{rate}", yearly?.rate || 0);
    const url = window.location.origin;
    const imgUrl = `${API}/public/year-recap-image.png?year=${yr}`;
    try {
      const res = await fetch(imgUrl);
      const blob = await res.blob();
      const file = new File([blob], `findloop-bilan-${yr}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Findloop — Bilan ${yr}`, text, url });
        return;
      }
      if (navigator.share) { await navigator.share({ title: `Findloop — Bilan ${yr}`, text, url }); return; }
      window.open(imgUrl, "_blank", "noopener");
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank", "noopener");
    } catch (e) {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank", "noopener");
    }
  }

  const totals = data.totals || {};
  const reachedCount = (data.cities || []).filter((c) => c.target != null && (c.progress || 0) >= 100).length;
  const gTarget = data.global_target || 0;
  const gRate = data.global_rate || 0;
  const gProgress = data.global_progress || 0;
  const gReached = gTarget > 0 && gRate >= gTarget;
  const history = data.global_history || [];
  const yearly = data.yearly;
  const restituted = totals.restituted || 0;
  const milestone = Math.floor(restituted / 100) * 100;
  const nextMilestone = milestone + 100;
  const podium = [...(data.cities || [])]
    .filter((c) => c.total >= 2)
    .sort((a, b) => (b.rate - a.rate) || (b.restituted - a.restituted))
    .slice(0, 3);

  const celebrated = useRef(false);
  useEffect(() => {
    if (inView && !celebrated.current && (reachedCount > 0 || gReached || milestone >= 100)) {
      celebrated.current = true;
      setTimeout(() => fireConfetti(true), 400);
    }
  }, [inView, reachedCount, gReached, milestone]);

  async function shareRanking() {
    const top = podium.length ? podium : (data.cities || []).slice(0, 3);
    const medals = ["🥇", "🥈", "🥉"];
    const lines = top.map((c, i) => `${medals[i]} ${c.city} (${c.rate}%)`).join("  ");
    const text = `Villes championnes de la restitution d'objets sur findloop : ${lines}`;
    const url = window.location.origin;
    const imgUrl = `${API}/public/impact-ranking-image.png`;

    // Try sharing the generated image as a file (Web Share API level 2).
    try {
      const res = await fetch(imgUrl);
      const blob = await res.blob();
      const file = new File([blob], "findloop-classement-villes.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: t("hc.findoCityRanking"), text, url });
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: t("hc.findoCityRanking"), text, url });
        return;
      }
      // Desktop fallback: open the image + a pre-filled X/Twitter post.
      window.open(imgUrl, "_blank", "noopener");
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank", "noopener");
    } catch (e) {
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, "_blank", "noopener");
    }
  }

  return (
    <section ref={sectionRef} className="max-w-6xl mx-auto px-6 py-20" data-testid="public-impact-section">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="inline-flex items-center gap-1.5 text-findo-red font-semibold text-sm uppercase tracking-wide">
          <MapPin size={15} /> {TT("impact.eyebrow", "Notre impact")}
        </span>
        <h2 className="font-display text-3xl md:text-4xl font-semibold text-findo-navy mt-3">
          {TT("impact.title", "Des objets rendus, partout près de chez vous")}
        </h2>
        <p className="mt-3 text-slate-500">
          {totals.restituted ? (
            <>{TT("impact.already", "Déjà")} <AnimatedCount value={totals.restituted} start={inView} /> {TT("impact.restitutedTo", "objets restitués à leurs propriétaires")}
            {totals.cities ? <> {TT("impact.inCities", "dans")} <b className="text-findo-navy">{totals.cities}</b> {TT("impact.citiesWord", "ville(s)")}</> : null}.</>
          ) : (
            <>{TT("impact.eachPoint", "Chaque point représente une ville où findloop aide à retrouver des objets perdus.")}</>
          )}
        </p>
        {reachedCount > 0 && (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-4 py-1.5 text-sm font-semibold text-amber-800" data-testid="impact-reached-badge">
            🎉 {t(reachedCount > 1 ? "hc.citiesAboveTarget" : "hc.cityAboveTarget").replace("{n}", reachedCount)}
          </div>
        )}
        {restituted > 0 && (
          <div className="mt-3 text-sm" data-testid="impact-milestone">
            {milestone >= 100 ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-findo-red/10 text-findo-red font-semibold px-4 py-1.5">
                🎊 {t("hc.milestoneFmt").replace("{m}", milestone).replace("{next}", nextMilestone)}
              </span>
            ) : (
              <span className="text-slate-400">
                {t("hc.only")} <b className="text-findo-navy">{100 - restituted}</b> {t("hc.toReachOurFirstMilestone")}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="rounded-[2rem] overflow-hidden border border-border card-shadow bg-white p-3">
        <div className="relative">
          <div ref={mapRef} className="w-full rounded-[1.6rem] overflow-hidden" style={{ height: 380, zIndex: 0 }} data-testid="public-impact-map" />
          {geo.length === 0 && (
            <div className="absolute inset-0 grid place-items-center bg-white/85 rounded-[1.6rem] text-slate-400 text-sm" data-testid="public-impact-empty">
              {TT("impact.emptyMap", "La carte se remplit au fur et à mesure des restitutions.")}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-5 mt-5 text-xs text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: rateColor(15) }} /> {t("hc.lowRate")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: rateColor(55) }} /> {t("hc.medium")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: rateColor(90) }} /> {t("hc.high")}</span>
      </div>

      {gTarget > 0 && (
        <div className="mt-10 rounded-[1.5rem] border border-border card-shadow bg-white p-6" data-testid="global-target-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 font-semibold text-findo-navy">
              <Target size={17} className="text-findo-red" /> {t("hc.platformReturnTarget")}
            </span>
            <span className={`text-sm font-bold ${gReached ? "text-emerald-600" : "text-findo-navy"}`} data-testid="global-target-progress">
              {gRate}% / {gTarget}% {gReached && "🎉"}
            </span>
          </div>
          <div className="mt-3 h-3 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-1000 ${gReached ? "bg-emerald-500" : "bg-findo-navy"}`}
              style={{ width: `${inView ? Math.min(100, gProgress) : 0}%` }} />
          </div>
          <p className="mt-2 text-xs text-slate-400">
            {gReached ? t("hc.targetReachedThankYouTo") : t("hc.pointsToGoal").replace("{n}", Math.max(0, (gTarget - gRate)).toFixed(1))}
          </p>

          {history.length > 0 && (
            <div className="mt-5 pt-4 border-t border-border" data-testid="global-history">
              <p className="text-xs font-semibold text-slate-500 mb-3">{t("hc.overallRateTrendLast6")}</p>
              <div className="flex items-end justify-between gap-2 h-24">
                {history.map((h, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center justify-end h-full" data-testid="history-bar">
                    <span className="text-[10px] font-semibold text-slate-500 mb-1">{h.total ? `${h.rate}%` : "—"}</span>
                    <div className="w-full rounded-t-md bg-findo-navy/80 transition-all duration-700"
                      style={{ height: `${inView ? Math.max(3, (h.rate / 100) * 70) : 0}px` }} title={`${h.rate}%`} />
                    <span className="text-[10px] text-slate-400 mt-1 capitalize">{h.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {yearly && yearly.target > 0 && (
        <div className="mt-6 rounded-[1.5rem] border border-border card-shadow bg-findo-navy text-white p-6" data-testid="yearly-target-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 font-semibold">
              <CalendarDays size={17} className="text-amber-300" /> Objectif annuel {yearly.year}
            </span>
            <span className="text-sm font-bold" data-testid="yearly-target-progress">
              {yearly.rate}% / {yearly.target}% {yearly.rate >= yearly.target && "🎉"}
            </span>
          </div>
          <div className="mt-3 h-3 rounded-full bg-white/15 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-1000 ${yearly.rate >= yearly.target ? "bg-emerald-400" : "bg-amber-300"}`}
              style={{ width: `${inView ? Math.min(100, yearly.progress) : 0}%` }} />
          </div>
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-white/60">{yearly.restituted} {t("hc.itemsReturnedThisYear")}</span>
            <span className="inline-flex items-center gap-1.5 font-semibold text-amber-300" data-testid="yearly-countdown">
              ⏳ {t("hc.daysToYearEnd").replace("{n}", yearly.days_remaining)}
            </span>
          </div>
          <div className="mt-4 flex justify-center gap-3 flex-wrap">
            <button onClick={shareYearRecap} data-testid="share-year-recap-btn"
              className="inline-flex items-center gap-2 rounded-full bg-white text-findo-navy text-sm font-semibold px-5 py-2.5 hover:bg-white/90 transition-colors">
              <Share2 size={15} /> {t("hc.shareReview")} {yearly.year}
            </button>
            <Link to="/retro" data-testid="view-retro-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white text-sm font-semibold px-5 py-2.5 hover:bg-white/10 transition-colors">
              ✨ {t("hc.seeRetro")} {yearly.year}
            </Link>
          </div>
        </div>
      )}

      {podium.length >= 3 && (
        <div className="mt-10 max-w-3xl mx-auto" data-testid="cities-podium">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Trophy size={17} className="text-findo-red" />
            <h3 className="font-display text-xl font-semibold text-findo-navy">{t("hc.topCitiesForReturns")}</h3>
          </div>
          <div className="grid grid-cols-3 gap-3 items-end">
            {[podium[1], podium[0], podium[2]].map((c, i) => {
              const rank = i === 1 ? 1 : i === 0 ? 2 : 3;
              const heights = { 1: "h-32", 2: "h-24", 3: "h-20" };
              const medals = { 1: "🥇", 2: "🥈", 3: "🥉" };
              return (
                <div key={c.city} className="flex flex-col items-center" data-testid={`podium-rank-${rank}`}>
                  <div className="text-2xl mb-1">{medals[rank]}</div>
                  <p className="text-sm font-bold text-findo-navy text-center truncate w-full">{c.city}</p>
                  <p className="text-xs text-slate-400 mb-2">{c.rate}% · {c.restituted} rendus</p>
                  <div className={`w-full ${heights[rank]} rounded-t-xl flex items-start justify-center pt-2 ${rank === 1 ? "bg-findo-red/90" : rank === 2 ? "bg-findo-navy/80" : "bg-findo-navy/50"}`}>
                    <span className="text-white font-display font-bold text-lg">{c.rate}%</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-center mt-6">
            <button onClick={shareRanking} data-testid="share-ranking-btn"
              className="inline-flex items-center gap-2 rounded-full bg-findo-navy text-white text-sm font-semibold px-5 py-2.5 hover:bg-findo-navy/90 transition-colors">
              <Share2 size={15} /> {t("hc.shareTheRanking")}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
